import { useEffect, useMemo, useRef, useState } from "react";
import type { RichChallengeProps } from "./richChallenges.ts";

// Rich, multimedia renderer for challenge_type === "laughing_man"
// (Ghost in the Shell — The Laughing Man / Stand Alone Complex).
//
// It uses the SAME flow as every other challenge: the prompt/content is
// authored server-side and arrives in `payload` (a JSON string), the player's
// answer is submitted through `onSubmit` and validated server-side, and the
// success clue comes back in `response`. The only thing that's "rich" is the
// presentation — here, the animated Laughing Man logo (the Salinger quote
// rotating around the rim), glitching feeds, and streamed narration.

interface Payload {
  logoText?: string; // the line that rotates around the logo
  subhead?: string; // e.g. "STAND ALONE COMPLEX"
  lines?: string[]; // narration, streamed in one at a time
  prompt?: string; // the question above the input
  placeholder?: string;
}

export function LaughingMan(props: RichChallengeProps) {
  const { payload, response, onSubmit, onClose } = props;
  const data = useMemo<Payload>(() => {
    try {
      return JSON.parse(payload || "{}");
    } catch {
      return {};
    }
  }, [payload]);

  const logoText =
    data.logoText ||
    "I thought what I'd do was, I'd pretend I was one of those deaf-mutes";
  const ring = `${logoText}  •  `;
  const lines = data.lines || [];

  // Stream the narration lines in for a "feeds resolving" feel.
  const [revealed, setRevealed] = useState(0);
  useEffect(() => {
    setRevealed(0);
    const t = setInterval(
      () => setRevealed((n) => (n >= lines.length ? n : n + 1)),
      650,
    );
    return () => clearInterval(t);
  }, [payload]); // eslint-disable-line react-hooks/exhaustive-deps

  const [answer, setAnswer] = useState("");
  const won = response.startsWith("Correct");
  const wrong = response.startsWith("Incorrect");

  // Bump a "glitch" key each time a wrong answer comes back, to re-trigger the
  // shake/scramble animation.
  const [glitch, setGlitch] = useState(0);
  const lastResponse = useRef(response);
  useEffect(() => {
    if (response !== lastResponse.current) {
      if (response.startsWith("Incorrect")) setGlitch((g) => g + 1);
      lastResponse.current = response;
    }
  }, [response]);

  return (
    <div className="lm-overlay">
      <style>{LM_CSS}</style>
      <div className="lm-scanlines" />

      <div className={`lm-card ${won ? "lm-won" : ""}`} key={glitch}>
        <button className="lm-close" onClick={onClose} aria-label="Disconnect">
          ✕
        </button>

        {/* The animated Laughing Man logo */}
        <div className={`lm-logo ${wrong ? "lm-shake" : ""}`}>
          <svg viewBox="0 0 300 300" width="220" height="220" aria-hidden>
            <defs>
              <path
                id="lm-ring-path"
                d="M150,150 m-118,0 a118,118 0 1,1 236,0 a118,118 0 1,1 -236,0"
                fill="none"
              />
            </defs>
            <g className="lm-spin">
              <text className="lm-ringtext">
                <textPath href="#lm-ring-path" startOffset="0">
                  {ring}
                  {ring}
                </textPath>
              </text>
            </g>
            {/* face */}
            <circle
              className="lm-face"
              cx="150"
              cy="150"
              r="84"
              fill="none"
            />
            {/* happy closed eyes ^ ^ */}
            <path
              className="lm-stroke"
              d="M108,138 q14,-20 28,0"
              fill="none"
            />
            <path
              className="lm-stroke"
              d="M164,138 q14,-20 28,0"
              fill="none"
            />
            {/* wide smile */}
            <path
              className="lm-stroke"
              d="M104,168 q46,52 92,0"
              fill="none"
            />
          </svg>
        </div>

        <div className="lm-subhead">{data.subhead || "STAND ALONE COMPLEX"}</div>

        <div className="lm-feed">
          {lines.slice(0, revealed).map((l, i) => (
            <div className="lm-line" key={i}>
              <span className="lm-prefix">⟶</span> {l}
            </div>
          ))}
          {revealed < lines.length && <div className="lm-cursor">▌</div>}
        </div>

        {won ? (
          <div className="lm-victory">
            {response.replace(/^Correct answer\.\s*/, "")}
            <div className="lm-victory-foot">
              <button className="lm-btn" onClick={onClose}>
                Close
              </button>
            </div>
          </div>
        ) : (
          <>
            <div className="lm-prompt">
              {data.prompt || "WHO IS BEHIND THE LOGO?"}
            </div>
            <div className="lm-inputrow">
              <input
                className="lm-input"
                value={answer}
                autoFocus
                placeholder={data.placeholder || "name him"}
                onChange={(e) => setAnswer(e.target.value)}
                onKeyDownCapture={(e) => {
                  e.stopPropagation();
                  if (e.key === "Enter" && answer.trim())
                    onSubmit(answer.trim());
                }}
              />
              <button
                className="lm-btn lm-go"
                onClick={() => answer.trim() && onSubmit(answer.trim())}
              >
                DECRYPT
              </button>
            </div>
            {response && (
              <div className={`lm-resp ${wrong ? "lm-resp-bad" : ""}`}>
                {response}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

const LM_CSS = `
.lm-overlay{position:fixed;inset:0;z-index:2000;display:flex;align-items:center;
  justify-content:center;background:radial-gradient(circle at 50% 40%,
  rgba(10,22,34,0.92),rgba(2,4,6,0.96));
  font-family:'MelGame','Courier New',monospace;overflow:hidden;}
.lm-scanlines{position:absolute;inset:0;pointer-events:none;opacity:0.18;
  background:repeating-linear-gradient(to bottom,rgba(255,255,255,0.10) 0 1px,
  transparent 1px 3px);animation:lm-roll 8s linear infinite;}
@keyframes lm-roll{to{background-position:0 120px;}}
.lm-card{position:relative;width:min(820px,92vw);height:min(660px,88vh);
  display:flex;flex-direction:column;overflow:hidden;
  text-align:center;color:var(--ro-text,#dfe9ef);
  background:linear-gradient(180deg,#06121c,#04080c);
  border:1px solid var(--ro-accent,#1FA8E0);border-radius:10px;padding:22px 22px 18px;
  box-shadow:0 0 40px rgba(31,168,224,0.35),0 18px 60px rgba(0,0,0,0.7);}
.lm-close{position:absolute;top:10px;right:12px;background:none;border:none;
  color:var(--ro-text-dim,#8aa0ad);font-size:22px;cursor:pointer;}
.lm-logo{display:flex;justify-content:center;margin:4px 0 2px;}
.lm-spin{transform-origin:150px 150px;animation:lm-rotate 18s linear infinite;}
@keyframes lm-rotate{to{transform:rotate(360deg);}}
.lm-ringtext{fill:var(--ro-accent,#1FA8E0);font-size:15.5px;letter-spacing:1.5px;
  font-family:'MelGame','Courier New',monospace;text-transform:uppercase;}
.lm-face,.lm-stroke{stroke:var(--ro-accent-bright,#7fd3f5);stroke-width:5;
  stroke-linecap:round;filter:drop-shadow(0 0 6px rgba(31,168,224,0.7));}
.lm-subhead{margin-top:6px;letter-spacing:0.42em;font-size:16px;
  color:var(--ro-accent-bright,#7fd3f5);text-shadow:0 0 10px rgba(31,168,224,0.6);}
.lm-feed{flex:1 1 auto;min-height:0;overflow-y:auto;
  margin:14px 2px;text-align:left;font-size:21px;
  color:var(--ro-text,#cdd8df);line-height:1.5;}
.lm-line{padding:2px 0;}
.lm-prefix{color:var(--ro-accent,#1FA8E0);}
.lm-cursor{color:var(--ro-accent,#1FA8E0);animation:lm-blink 1s steps(2) infinite;}
@keyframes lm-blink{50%{opacity:0;}}
.lm-prompt{margin:8px 0 8px;letter-spacing:0.16em;font-size:20px;
  color:var(--ro-accent-bright,#7fd3f5);}
.lm-inputrow{display:flex;gap:8px;}
.lm-input{flex:1;background:#020608;border:1px solid var(--ro-border,#274250);
  color:var(--ro-text,#e6f0f5);padding:9px 11px;border-radius:5px;
  font-family:inherit;font-size:22px;outline:none;}
.lm-input:focus{border-color:var(--ro-accent,#1FA8E0);
  box-shadow:0 0 0 2px rgba(31,168,224,0.25);}
.lm-btn{background:#08222f;border:1px solid var(--ro-accent,#1FA8E0);
  color:var(--ro-accent-bright,#7fd3f5);padding:9px 16px;border-radius:5px;
  cursor:pointer;font-family:inherit;font-size:19px;letter-spacing:0.12em;}
.lm-btn:hover{background:#0c3346;}
.lm-go{font-weight:700;}
.lm-resp{margin-top:10px;font-size:19px;font-style:italic;
  color:var(--ro-text-dim,#9fb4c0);}
.lm-resp-bad{color:#ff8a8a;font-style:normal;}
.lm-victory{margin-top:12px;white-space:pre-wrap;text-align:left;font-size:21px;
  color:var(--ro-accent-bright,#9fe0ff);background:rgba(31,168,224,0.08);
  border:1px solid var(--ro-accent,#1FA8E0);border-radius:6px;padding:14px;}
.lm-victory-foot{text-align:right;margin-top:10px;}
.lm-won .lm-face,.lm-won .lm-stroke{stroke:#7CFFB0;
  filter:drop-shadow(0 0 8px rgba(124,255,176,0.8));}
.lm-won .lm-ringtext{fill:#7CFFB0;}
.lm-shake{animation:lm-shake 0.4s linear;}
@keyframes lm-shake{0%,100%{transform:translateX(0);}
  20%{transform:translateX(-6px) skewX(-3deg);}40%{transform:translateX(6px);}
  60%{transform:translateX(-4px) skewX(2deg);}80%{transform:translateX(4px);}}
`;

export default LaughingMan;
